<template>
	<div :style="{ '--container-width': containerWidth + 'px', '--carousel-color': colorRgb }">
		<div class="section pt-0 pb-32">
			<div ref="headerContainer" class="container flex gap-8 items-center">
				<h3 class="flex-1 text-[rgb(var(--carousel-color))]">{{ heading }}</h3>
				<div class="flex gap-4 items-center">
					<button
						@click="go('<')"
						:disabled="atStart"
						class="size-32 rounded-full flex items-center justify-center text-[rgb(var(--carousel-color))] opacity-50 hocus:opacity-100 hocus:bg-[rgb(var(--carousel-color)_/_0.1)] transition-colors duration-300 ease-in-out cursor-pointer disabled:pointer-events-none"
						aria-label="Previous slide"
					>
						<div class="size-24">
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 6-6 6 6 6"/></svg>
						</div>
					</button>
					<button
						@click="go('>')"
						:disabled="atEnd"
						class="size-32 rounded-full flex items-center justify-center text-[rgb(var(--carousel-color))] opacity-50 hocus:opacity-100 hocus:bg-[rgb(var(--carousel-color)_/_0.1)] transition-colors duration-300 ease-in-out cursor-pointer disabled:pointer-events-none"
						aria-label="Next slide"
					>
						<div class="size-24">
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 6 6 6-6 6"/></svg>
						</div>
					</button>
				</div>
			</div>
		</div>
		<Splide ref="splideRef" :options="splideOptions" :has-track="false" :aria-label="heading">
			<SplideTrack>
				<slot></slot>
			</SplideTrack>
		</Splide>
	</div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { Splide, SplideTrack } from '@splidejs/vue-splide';
import tailwindConfig from '../../../../config/frontend/tailwind.config.js';

const themeColors = tailwindConfig.theme.colors;

const props = defineProps({
	heading: {
		type: String,
		default: 'Homes Carousel',
	},
	color: {
		type: String,
		default: 'white',
	},
});

const colorRgb = computed(() => {
	const parts = props.color.split('-');
	let hex = parts.length === 1 ? themeColors[parts[0]] : themeColors[parts[0]]?.[parts[1]];
	if (!hex) hex = '#000000';
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `${r} ${g} ${b}`;
});

const splideRef = ref(null);
const headerContainer = ref(null);
const containerWidth = ref(0);
const windowWidth = ref(window.innerWidth);
const atStart = ref(true);
const atEnd = ref(false);

const currentGap = computed(() => {
	if (windowWidth.value < 768) return 16;
	if (windowWidth.value < 1024) return 20;
	return 24;
});

const splideOptions = reactive({
	type: 'slide',
	gap: '24px',
	autoWidth: true,
	pagination: false,
	arrows: false,
	speed: 400,
	easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
	padding: { left: 0 },
});

function calcPadding() {
	if (!headerContainer.value) return;
	const rect = headerContainer.value.getBoundingClientRect();
	containerWidth.value = rect.width;
	splideOptions.padding = {
		left: rect.left,
		right: window.innerWidth - rect.right,
	};
}

function getVisibleCount() {
	const splide = splideRef.value?.splide;
	if (!splide) return 1;
	const slide = splide.Components.Elements.slides[0];
	if (!slide) return 1;
	const slideWidth = slide.getBoundingClientRect().width;
	const gap = currentGap.value;
	return Math.max(1, Math.floor((containerWidth.value + gap) / (slideWidth + gap)));
}

function updateArrows() {
	const splide = splideRef.value?.splide;
	if (!splide) return;
	atStart.value = splide.index <= 0;
	atEnd.value = splide.index >= splide.length - getVisibleCount();
}

let resizeObserver;

onMounted(() => {
	calcPadding();
	splideOptions.gap = currentGap.value + 'px';
	resizeObserver = new ResizeObserver(() => {
		windowWidth.value = window.innerWidth;
		splideOptions.gap = currentGap.value + 'px';
		calcPadding();
		updateArrows();
	});
	resizeObserver.observe(document.documentElement);

	const splide = splideRef.value?.splide;
	if (splide) {
		splide.on('mounted moved', updateArrows);
		updateArrows();
	}
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
});

function go(control) {
	const splide = splideRef.value?.splide;
	if (!splide) return;

	if (control === '>' && splide.index >= splide.length - getVisibleCount()) return;

	splide.go(control);
}
</script>
